import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Alert,
  Chip,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrg } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { LayoutRecord } from "@/shared/domain/layouts";
import { PaginatedResponse } from "@/shared/http";

import { RobotoButton } from "../../RobotoButton";
import { RobotoTypography } from "../../RobotoTypography";

interface LayoutConflictModalProps {
  conflictingLayouts: {
    userLayout: LayoutRecord;
    sharedLayout: LayoutRecord;
  } | null;
  open: boolean;
  onClose: () => void;
  onLayoutRenamed?: (renamedLayout: LayoutRecord) => void;
}

export function LayoutConflictModal(props: LayoutConflictModalProps) {
  const { conflictingLayouts, open, onClose, onLayoutRenamed } = props;

  const [newName, setNewName] = React.useState("");

  const renameLayoutMutation = useRenameLayout();

  const userLayout = conflictingLayouts?.userLayout;
  const sharedLayout = conflictingLayouts?.sharedLayout;

  React.useEffect(() => {
    if (open && userLayout) {
      // Suggest a new name by appending "-personal"
      const baseName = userLayout.name;
      const suggestedName = `${baseName}-personal`;
      setNewName(suggestedName);
    }
  }, [userLayout, open]);

  const handleRename = () => {
    const trimmedName = newName.trim();

    if (!userLayout || !trimmedName) {
      return;
    }

    renameLayoutMutation.mutate(
      {
        layoutId: userLayout.layout_id,
        newName: trimmedName,
      },
      {
        onSuccess: (renamedLayout: LayoutRecord) => {
          // Set the renamed layout as the active layout
          onLayoutRenamed?.(renamedLayout);
          onClose();
        },
      },
    );
  };

  const handleCancel = () => {
    setNewName("");
    onClose();
  };

  if (!userLayout || !sharedLayout) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Layout Name Conflict</DialogTitle>
      <DialogContent>
        <RobotoTypography variant="body1" sx={{ mb: 2 }}>
          You have both a personal and shared layout named{" "}
          <strong>&quot;{userLayout.name}&quot;</strong>. To avoid confusion,
          please rename your personal layout.
        </RobotoTypography>

        <Box sx={{ mb: 3 }}>
          <RobotoTypography variant="subtitle2" sx={{ mb: 1 }}>
            Conflicting layouts:
          </RobotoTypography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label="Personal"
                size="small"
                color="primary"
                variant="outlined"
              />
              <Typography variant="body2">
                {userLayout.name} (created by you)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label="Shared"
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Typography variant="body2">
                {sharedLayout.name} (shared with team)
              </Typography>
            </Box>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="New name for your personal layout"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={renameLayoutMutation.isPending}
          autoFocus
          sx={{ mb: 2 }}
        />

        {renameLayoutMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {renameLayoutMutation.error.message}
          </Alert>
        )}

        <RobotoTypography variant="body2" color="text.secondary">
          After renaming, the shared layout &quot;{sharedLayout.name}&quot; will
          be displayed by default when accessing this layout by name.
        </RobotoTypography>
      </DialogContent>
      <DialogActions>
        <RobotoButton
          eventName="LayoutConflictModalCanceled"
          onClick={handleCancel}
          disabled={renameLayoutMutation.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="LayoutConflictModalRenamed"
          eventProperties={{
            layoutName: newName,
          }}
          onClick={handleRename}
          variant="contained"
          disabled={!newName.trim() || renameLayoutMutation.isPending}
        >
          {renameLayoutMutation.isPending
            ? "Renaming..."
            : "Rename Personal Layout"}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}

interface RenameLayoutRequest {
  layoutId: string;
  newName: string;
}

function useRenameLayout() {
  const { layoutService } = useDomainServices();
  const currentOrganization = useCurrentOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ layoutId, newName }: RenameLayoutRequest) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return layoutService.updateLayout(
        layoutId,
        { name: newName },
        currentOrganization.org_id,
      );
    },
    onSuccess: async (layoutRecord: LayoutRecord) => {
      await queryClient.setQueryData(
        ["layout", layoutRecord.layout_id],
        layoutRecord,
      );
      return queryClient.setQueryData<PaginatedResponse<LayoutRecord>>(
        ["layouts"],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          return {
            ...oldData,
            items: oldData.items.map((record) => {
              if (record.layout_id === layoutRecord.layout_id) {
                return layoutRecord;
              }
              return record;
            }),
          };
        },
      );
    },
  });
}
